import { Link } from "react-router-dom";
import { ShieldCheck, Target, Users, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const VALUES = [
    {
        icon: Target,
        title: "Our Mission",
        desc: "To make tender participation effortless for every Indian business — from solo contractors to large bid teams — by turning 100-page tender PDFs into clear, actionable insights in seconds.",
    },
    {
        icon: Sparkles,
        title: "What We Do",
        desc: "TenderTrackAI uses advanced AI to read tender documents and instantly extract deadlines, EMD amounts, eligibility criteria, required documents and risk factors — with summaries in English and Indian languages.",
    },
    {
        icon: Users,
        title: "Who We Serve",
        desc: "Government contractors, private bidders, MSMEs and startups across India who want to bid on more tenders without drowning in paperwork.",
    },
    {
        icon: ShieldCheck,
        title: "How We Operate",
        desc: "Transparent credit-based pricing (Free, Starter ₹299, Pro ₹499, Business ₹999 per month), secure encrypted infrastructure, and payments processed through Razorpay.",
    },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <section className="pt-20 pb-12 relative">
                <div className="absolute inset-0 mesh-bg opacity-60" />
                <div className="container-tight relative text-center max-w-3xl">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400">
                        About Us
                    </p>
                    <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 dark:text-slate-50 text-balance">
                        The AI co-pilot for India&apos;s bid teams.
                    </h1>
                    <p className="mt-5 text-lg text-slate-600 dark:text-slate-400">
                        TenderTrackAI Technologies Pvt. Ltd. is a Bengaluru-based software
                        company building AI tools that help businesses understand and win
                        tenders faster.
                    </p>
                </div>
            </section>

            <section className="pb-16">
                <div className="container-tight max-w-5xl">
                    <div className="grid sm:grid-cols-2 gap-6">
                        {VALUES.map((v) => (
                            <div
                                key={v.title}
                                className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-7"
                            >
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400">
                                    <v.icon className="h-5 w-5" />
                                </div>
                                <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-slate-50">
                                    {v.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                                    {v.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="pb-24">
                <div className="container-tight max-w-3xl">
                    <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 lg:p-10">
                        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">
                            Business &amp; Registered Details
                        </h2>
                        <div className="mt-5 space-y-2 text-sm text-slate-600 dark:text-slate-400">
                            <p>
                                <strong className="text-slate-900 dark:text-slate-100">Legal name:</strong>{" "}
                                TenderTrackAI Technologies Pvt. Ltd.
                            </p>
                            <p>
                                <strong className="text-slate-900 dark:text-slate-100">Address:</strong>{" "}
                                Indiranagar, Bengaluru 560038, Karnataka, India
                            </p>
                            <p>
                                <strong className="text-slate-900 dark:text-slate-100">Email:</strong>{" "}
                                support@tendertrackai.com
                            </p>
                            <p>
                                <strong className="text-slate-900 dark:text-slate-100">Phone:</strong>{" "}
                                +91 80-4444-2200 (Mon–Sat, 9am–7pm IST)
                            </p>
                            <p>
                                <strong className="text-slate-900 dark:text-slate-100">Product:</strong>{" "}
                                AI tender analysis software, sold as monthly subscription plans
                                with analysis credits.{" "}
                                <Link to="/pricing" className="text-indigo-600 hover:underline">
                                    See pricing →
                                </Link>
                            </p>
                        </div>
                        <div className="mt-7 flex flex-col sm:flex-row gap-3">
                            <Link to="/contact">
                                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto">
                                    Contact Us <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </Link>
                            <Link to="/pricing">
                                <Button variant="outline" className="w-full sm:w-auto">
                                    View Plans &amp; Pricing
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
